import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Tooltip,
  Typography,
  useScrollTrigger,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import OpenCardDetail from "../openCardDetail";
import CreateCard from "../CreateCard";
import KanbanCard from "../../../../../components/Kanban/KanbanCard";
import MobileTodo from "./MobileTodo";
import { TooltipName } from "../../../../../utils/Enums";
import "./mobileKanban.scss";
import { toast } from "react-hot-toast";
import { CircularProgress } from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import ToDo from "../../../../../components/ToDo";
import AddIcon from "@mui/icons-material/Add";

const MobileKanban = ({
  workflow: initialWorkflow,
  data,
  cards,
  createCard = false,
  isMyTaskView = false,
}) => {
  // Memoize initial workflow state

  // Initialize states
  const [projects, setProjects] = useState(cards);
  const [workflow, setWorkflow] = useState(
    initialWorkflow.sort((a, b) => a.step_sequence - b.step_sequence)
  );

  // Memoize expanded sections initialization
  const initialExpandedSections = useMemo(() => {
    const sections = new Set();
    const todoProjects = cards.filter((card) => card.card.is_todo);
    if (todoProjects.length > 0) {
      sections.add("quick-tasks");
    }
    initialWorkflow?.forEach((column) => {
      const hasCards = cards.some(
        (card) => card.card.workflow_step_id === column.workflow_step_id
      );
      if (hasCards) {
        sections.add(column.workflow_step_id.toString());
      }
    });
    return sections;
  }, [cards, initialWorkflow]);

  const [expandedSections, setExpandedSections] = useState(
    initialExpandedSections
  );
  // Get search params and other state
  const [searchParams] = useSearchParams();
  const searchText = useSelector((state) => state.search.searchText);
  const trigger = useScrollTrigger();
  const isMobile = useResponsive("down", "sm");
  const [loading, setLoading] = useState(false);
  const [isCreateCardFieldOpen, setIsCreateCardFieldOpen] = useState([]);
  const containerRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);

  const filterParams = useMemo(
    () => ({
      filterUser: searchParams.get("search")?.split(","),
      epicSearch: searchParams.get("milestone"),
      noMileStoneSearch: searchParams.get("noMilestone"),
      cardId: searchParams.get("card_id"),
      worklogs: searchParams.get("worklogs"),
    }),
    [searchParams]
  );

  // Priority ordering for card sorting
  const priorityOrder = useMemo(
    () => ({
      Critical: 1,
      High: 2,
      Medium: 3,
      Low: 4,
    }),
    []
  );

  // Memoize filter function
  const filterCards = useCallback(
    (cardsToFilter) => {
      let filteredCards = [...cardsToFilter];

      if (filterParams.filterUser?.length) {
        const numericFilterUser = filterParams.filterUser.map(Number);
        filteredCards = filteredCards.filter((el) =>
          numericFilterUser.includes(el?.card?.assigned_to_user_id)
        );
      }

      if (filterParams.epicSearch) {
        filteredCards = filteredCards.filter(
          (item) => filterParams.epicSearch == item.card.epic_id
        );
      }

      if (filterParams.noMileStoneSearch === "true") {
        filteredCards = filteredCards.filter(
          (item) => item.card.epic_id == null
        );
      }

      if (searchText) {
        filteredCards = filteredCards.filter(
          (el) =>
            el?.card.title?.toLowerCase().includes(searchText.toLowerCase()) ||
            el?.card.assigned_to_user?.first_name
              ?.toLowerCase()
              .includes(searchText.toLowerCase())
        );
      }

      return filteredCards;
    },
    [filterParams, searchText]
  );

  // Update projects when cards or filters change
  useEffect(() => {
    const debouncedFilter = debounce(() => {
      const filteredCards = filterCards(cards);
      setProjects(filteredCards);

      // Update workflow with new filtered cards
      setWorkflow((prev) =>
        prev.map((column) => ({
          ...column,
          cards: filteredCards.filter(
            (card) => card.card.workflow_step_id === column.workflow_step_id
          ),
        }))
      );
    }, 300);

    debouncedFilter();

    return () => {
      debouncedFilter.cancel();
    };
  }, [cards, filterCards]);

  // Memoize drag end handler
  const onDragEnd = useCallback((result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (destination.droppableId === "quick-tasks") {
      toast.error("Cannot move a Regular task to Quick Tasks");
      return;
    }

    setWorkflow((prevWorkflow) => {
      const sourceColumn = prevWorkflow.find(
        (col) => col.workflow_step_id.toString() === source.droppableId
      );
      const destinationColumn = prevWorkflow.find(
        (col) => col.workflow_step_id.toString() === destination.droppableId
      );

      if (!sourceColumn || !destinationColumn) return prevWorkflow;

      const newSourceCards = [...sourceColumn.cards];
      const newDestinationCards = [...destinationColumn.cards];
      const [movedCard] = newSourceCards.splice(source.index, 1);

      movedCard.card.workflow_step_id = parseInt(destination.droppableId);
      newDestinationCards.splice(destination.index, 0, movedCard);

      return prevWorkflow.map((col) => {
        if (col.workflow_step_id.toString() === source.droppableId) {
          return { ...col, cards: newSourceCards };
        }
        if (col.workflow_step_id.toString() === destination.droppableId) {
          return { ...col, cards: newDestinationCards };
        }
        return col;
      });
    });

    setProjects((prev) =>
      prev.map((project) => {
        if (project.card.card_id === parseInt(draggableId)) {
          return {
            ...project,
            card: {
              ...project.card,
              workflow_step_id: parseInt(destination.droppableId),
            },
          };
        }
        return project;
      })
    );
  }, []);

  // Sort projects by priority
  const sortByPriority = (projects) => {
    return projects?.sort(
      (a, b) =>
        priorityOrder[a?.card?.priority] - priorityOrder[b?.card?.priority]
    );
  };

  // Get todo projects
  const todoProjects =
    sortByPriority(cards.filter((card) => card.card.is_todo)) || [];

  const preventing = (e) => {
    e.preventDefault();
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      const newSections = new Set(prev);
      if (newSections.has(sectionId)) {
        newSections.delete(sectionId);
      } else {
        newSections.add(sectionId);
      }
      return newSections;
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const sections = container.getElementsByClassName(
        "mobile-accordion-section"
      );
      let currentSection = null;

      for (let section of sections) {
        const summary = section.querySelector(".MuiAccordionSummary-root");
        if (!summary) continue;

        const rect = summary.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (rect.top <= containerRect.top && rect.bottom >= containerRect.top) {
          currentSection = section;
          break;
        }
      }
      setActiveSection(currentSection);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const groupedCardByWorkFlowSteps = cards.reduce((acc, item) => {
    const stepId = item?.card?.workflow_step_id;
    const genStepId = item?.generic_step_id;

    // Use generic step ID if available, otherwise use workflow step ID
    const targetId = isMyTaskView ? genStepId || stepId : stepId;

    if (!acc[targetId]) {
      acc[targetId] = [];
    }
    acc[targetId].push(item);
    return acc;
  }, {});

  const handleCreateCardClick = (event, sectionId) => {
    event?.stopPropagation();
    setIsCreateCardFieldOpen((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId); // Remove sectionId if it already exists
      } else {
        return [...prev, sectionId]; // Add sectionId if it doesn't exist
      }
    });
  };

  return (
    <Box pt={0}>
      {projects && filterParams.cardId && (
        <OpenCardDetail cardsOnBoard={projects} />
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="mobile-kanban" ref={containerRef}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <CircularProgress />
            </div>
          ) : (
            <div className="mobile-kanban-content">
              {/* Quick Tasks Section */}

              <Accordion
                expanded={expandedSections.has("quick-tasks")}
                onChange={() => toggleSection("quick-tasks")}
                className={`mobile-accordion-section quick-tasks-mobile-cont ${
                  activeSection?.querySelector(".mobile-column-header-h4")
                    ?.textContent === "Quick Tasks"
                    ? "active"
                    : ""
                }
                   ${
                     expandedSections.has("quick-tasks")
                       ? ""
                       : "not-expand-quick-task"
                   }
                  `}
              >
                <AccordionSummary
                  expandIcon={<KeyboardArrowDown />}
                  className="mobile-accordion-header"
                >
                  <div className="mobile-header-content">
                    <div className="mobile-column-header">
                      <h4 className="mobile-column-header-h4">Quick Tasks</h4>
                      <span className="mobile-column-header-length">
                        {todoProjects.length}
                      </span>
                    </div>
                    {!isCreateCardFieldOpen.includes("todo-column") &&
                      !isMyTaskView && (
                        <Box className="mobile-create-card-btn">
                          <AddIcon
                            onClick={(event) =>
                              handleCreateCardClick(event, "todo-column")
                            }
                          />
                        </Box>
                      )}
                  </div>
                </AccordionSummary>
                <AccordionDetails className="mobile-accordion-content">
                  <div className="mobile-accordion-content-inner">
                    {/* <MobileTodo
                        projects={todoProjects}
                        droppableId="quick-tasks"
                      /> */}
                    <ToDo
                      name="Quick Tasks"
                      stage="todo-column"
                      projects={todoProjects}
                      height={270}
                      isCreateCardFieldOpen={isCreateCardFieldOpen.includes(
                        "todo-column"
                      )}
                      handleCreateCardClick={handleCreateCardClick}
                      // maxHeight={isTeam ? maxHeight : ""}
                      key={`quick-tasks-column`}
                      id={`quick-tasks-column`}
                    />
                    {todoProjects?.length === 0 && (
                      <div className="no-task-in-column">
                        <p>No Tasks in Quick Tasks.</p>
                      </div>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* Workflow Sections */}
              {workflow.map((column, index) => {
                const filteredProjects = sortByPriority(
                  groupedCardByWorkFlowSteps[column?.workflow_step_id] || []
                );
                const sectionId = column.workflow_step_id.toString();

                return (
                  <Accordion
                    key={column.workflow_step_id}
                    expanded={expandedSections.has(sectionId)}
                    onChange={() => toggleSection(sectionId)}
                    className={`mobile-accordion-section kanban-worflow-column ${
                      activeSection?.querySelector(".mobile-column-header-h4")
                        ?.textContent === column.status
                        ? "active"
                        : ""
                    } ${
                      expandedSections.has(sectionId) ||
                      index == 0 ||
                      index === workflow?.length - 1
                        ? ""
                        : "not-expanded"
                    } ${
                      workflow?.length == 1
                        ? "single-workflow-step"
                        : index == 0 && workflow?.length > 1
                        ? "first-workflow-step"
                        : index === workflow?.length - 1 && workflow?.length > 1
                        ? "last-workflow-step"
                        : ""
                    }`}
                  >
                    <AccordionSummary
                      expandIcon={<KeyboardArrowDown />}
                      className="mobile-accordion-header"
                    >
                      <div className="mobile-header-content">
                        <div className="mobile-column-header">
                          <h4 className="mobile-column-header-h4">
                            {column.status}
                          </h4>
                          <span className="mobile-column-header-length">
                            {filteredProjects.length}
                          </span>
                        </div>
                        {!isCreateCardFieldOpen.includes(sectionId) &&
                          !isMyTaskView && (
                            <Box className="mobile-create-card-btn">
                              <AddIcon
                                onClick={(event) =>
                                  handleCreateCardClick(event, sectionId)
                                }
                              />
                            </Box>
                          )}
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className="mobile-accordion-content">
                      <div className="mobile-accordion-content-inner">
                        {filteredProjects?.length === 0 && (
                          <div className="no-task-in-column">
                            <p>No Tasks in {column.status}</p>
                          </div>
                        )}
                        <MobileTodo
                          projects={filteredProjects}
                          droppableId={sectionId}
                          stage={column?.workflow_step_id}
                          isCreateCardFieldOpen={isCreateCardFieldOpen.includes(
                            sectionId
                          )}
                          handleCreateCardClick={handleCreateCardClick}
                          isMobile={isMobile}
                        />
                      </div>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          )}
        </div>
      </DragDropContext>
    </Box>
  );
};

const AccordionSection = ({
  title,
  count,
  isOpen,
  onToggle,
  children,
  onDragOver,
}) => {
  const [mouseIsHovering, setMouseIsHovering] = useState(false);
  const sectionRef = useRef(null);

  return (
    <div
      ref={sectionRef}
      className={`accordion-section ${
        mouseIsHovering ? "kanban-column-bg-1" : "kanban-column-bg-2"
      } ${isOpen ? "expanded" : ""}`}
      onDragEnter={() => setMouseIsHovering(true)}
      onDragLeave={() => setMouseIsHovering(false)}
      onDragOver={onDragOver}
    >
      <div className="accordion-header" onClick={onToggle}>
        <div className="header-content">
          <h4 className="coloumn-header-h4">
            <p className="coloumn-header-text">{title}</p>
            <Tooltip title={TooltipName.TICKETS_IN_COLUMN}>
              <span className="coloumn-header-length">{count}</span>
            </Tooltip>
          </h4>
        </div>
        <button className={`expand-button ${isOpen ? "open" : ""}`}>
          {isOpen ? "▼" : "▶"}
        </button>
      </div>
      {isOpen && <div className="accordion-content">{children}</div>}
    </div>
  );
};

export default React.memo(MobileKanban);
